import { useId } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { getTrunkHeight, Tree } from 'store/slices/TreeSlice';
import { convertFeetToMetric, convertMetricToFeet } from '../../util';
import { TreePreviewSvg, TreePreviewWrap } from './styles';

const VIEW_SIZE = 220;
const PADDING = 14;
// Crown diameter's label floats above the crown top — needs more headroom
// than the other sides so it isn't cramped against the card edge. Only
// reserved when that label is actually showing; otherwise the top uses the
// same padding as the bottom so the tree can use the full card.
const PADDING_TOP = 34;
// Height's own scale term never gets looser than availableH / this
// reference, even for a short tree — that's what stops a narrow tree from
// overflowing the card before treeHeight ever reaches the reference. 45ft
// is the point past which a tree is unusually tall, so that's where it
// starts trading height for scale.
const HEIGHT_FILL_REFERENCE_M = convertFeetToMetric(45, 'm', 6);
const TRUNK_BOTTOM_WIDEN = 1.1;
const ARROW_GAP = 10;
const ARROW_COLOR = 'var(--primary)';
const MIN_ARROW_HALF_SPAN = 6;

export type ActiveTreeField = 'crownDiameter' | 'trunkDiameter' | 'crownHeight' | 'treeHeight' | null;

interface TreePreview2DProps {
	tree: Pick<Tree, 'crownRadius' | 'crownHeight' | 'trunkRadius' | 'treeHeight' | 'crownShape'>;
	activeField?: ActiveTreeField;
}

export default function TreePreview2D({ tree, activeField = null }: TreePreview2DProps) {
	const clipId = `tree-preview-clip-${useId()}`;
	const groundClipId = `tree-preview-ground-clip-${useId()}`;
	const { groundMetersPerPixel } = useSelector((state: RootState) => state.roofData.data);
	const gmpp = groundMetersPerPixel || 1;
	const { crownShape } = tree;
	// Trunk mesh never draws negative — floors at 0 once crownHeight is
	// pushed past treeHeight (matches buildSingleTreeMesh). The crown itself
	// keeps its TRUE height though — see crownHeight below — so it renders
	// at full size and only gets clipped at the ground line, instead of
	// being squashed to always fit above ground.
	const trunkHeight = Math.max(0, getTrunkHeight(tree));
	const crownHeight = Math.max(0, tree.crownHeight);
	// crownRadius/trunkRadius are stored in DSM-pixel units (same space as
	// x/y); crownHeight/trunkHeight are stored in real meters. Convert the
	// radii to meters so proportions match the 3D preview (see
	// buildSingleTreeMesh in BuildTreeMeshes3D.ts).
	const crownRadius = tree.crownRadius * gmpp;
	const trunkRadius = tree.trunkRadius * gmpp;

	const trunkTopRadius = trunkRadius;
	const trunkBottomRadius = trunkRadius * TRUNK_BOTTOM_WIDEN;

	// The auto-fit scale is driven by the tree's visible ground-to-peak
	// extent (treeHeight) — not by crownHeight, which can run past that and
	// get clipped at the ground line (see crownBaseY below). Sizing the
	// scale off the unclipped crownHeight would shrink the whole tree just
	// to make room for a part that's never shown.
	const totalHeight = Math.max(tree.treeHeight, 0.0001);
	const maxHalfWidth = Math.max(crownRadius, trunkBottomRadius, 0.0001);

	const paddingTop = activeField === 'crownDiameter' ? PADDING_TOP : PADDING;
	const availableW = VIEW_SIZE - PADDING * 2;
	const availableH = VIEW_SIZE - paddingTop - PADDING;
	// Below HEIGHT_FILL_REFERENCE_M, height's scale term stays pinned to the
	// reference (a constant) instead of loosening further — a short tree
	// still ends up width-bound like before, but capping the plateau at the
	// reference's own scale is what guarantees a narrow tree can't overflow
	// the card before treeHeight actually reaches it. Past the reference,
	// height's term shrinks below that plateau and starts pulling `scale`
	// down for real.
	const heightScale = availableH / Math.max(totalHeight, HEIGHT_FILL_REFERENCE_M);
	const scale = Math.min(availableW / (maxHalfWidth * 2), heightScale);

	const cx = VIEW_SIZE / 2;
	const groundY = VIEW_SIZE - PADDING;

	const trunkTopY = groundY - trunkHeight * scale;
	const trunkTopHalfW = trunkTopRadius * scale;
	const trunkBottomHalfW = trunkBottomRadius * scale;

	// Peak pinned at treeHeight above ground, always — independent of how
	// crownHeight/trunkHeight trade off. The crown grows DOWNWARD from here
	// by its true height, so when crownHeight > treeHeight, crownBaseY
	// lands below groundY (below ground) — that overflow is what
	// groundClipId hides, matching the 3D reference's ground clip.
	const crownTopY = groundY - tree.treeHeight * scale;
	const crownHeightPx = crownHeight * scale;
	const crownHalfW = crownRadius * scale;
	const crownBaseY = crownTopY + crownHeightPx;
	// The crownHeight arrow should never visually cross the ground line —
	// it still shows the growing number via the label, just clamped to the
	// visible extent instead of dipping below the baseline.
	const crownArrowBottomY = Math.min(crownBaseY, groundY);

	const trunkPoints = [
		[cx - trunkBottomHalfW, groundY],
		[cx + trunkBottomHalfW, groundY],
		[cx + trunkTopHalfW, trunkTopY],
		[cx - trunkTopHalfW, trunkTopY],
	].map((p) => p.join(',')).join(' ');

	const isCone = crownShape === 'cone';

	// Matches the precision the slider fields show (see TreeHeightField etc.)
	// so the preview never rounds away from what the input actually holds.
	const crownDiameterFt = convertMetricToFeet(crownRadius * 2, 'm', 1);
	const trunkDiameterFt = convertMetricToFeet(trunkRadius * 2, 'm', 1);
	const crownHeightFt = convertMetricToFeet(crownHeight, 'm', 1);
	const treeHeightFt = convertMetricToFeet(tree.treeHeight, 'm', 1);

	return (
		<TreePreviewWrap>
			<TreePreviewSvg viewBox={`0 0 ${VIEW_SIZE} ${VIEW_SIZE}`} preserveAspectRatio="xMidYMid meet">
				<defs>
					<marker id="tree-preview-arrowhead" viewBox="0 0 10 10" refX="5" refY="5" markerWidth="6" markerHeight="6" orient="auto-start-reverse">
						<path d="M0,0 L10,5 L0,10 Z" fill={ARROW_COLOR} />
					</marker>
					{/* Belt-and-suspenders clip so the new measurement arrows/labels
						never render outside the card, even at extreme values. */}
					<clipPath id={clipId}>
						<rect x={0} y={0} width={VIEW_SIZE} height={VIEW_SIZE} />
					</clipPath>
					{/* The crown renders at its TRUE height and can dip below the
						ground line (see crownBaseY) — this clips that overflow
						exactly at groundY, matching the ground clamp in the 3D
						reference, while keeping the crown's above-ground portion
						at its natural, unsquashed proportions. */}
					<clipPath id={groundClipId}>
						<rect x={0} y={0} width={VIEW_SIZE} height={groundY} />
					</clipPath>
				</defs>
				<g clipPath={`url(#${clipId})`}>
					<line x1={PADDING / 2} y1={groundY} x2={VIEW_SIZE - PADDING / 2} y2={groundY} stroke="var(--cardBorderColor)" strokeWidth={1.5} />
					<polygon points={trunkPoints} fill="var(--treeTrunkColor)" />
					<g clipPath={`url(#${groundClipId})`}>
						{isCone ? (
							<polygon
								points={[
									[cx - crownHalfW, crownBaseY],
									[cx + crownHalfW, crownBaseY],
									[cx, crownTopY],
								].map((p) => p.join(',')).join(' ')}
								fill="var(--treeCrownColor)"
							/>
						) : (
							<ellipse
								cx={cx}
								cy={crownBaseY - crownHeightPx / 2}
								rx={crownHalfW}
								ry={crownHeightPx / 2}
								fill="var(--treeCrownColor)"
							/>
						)}
					</g>

					{activeField === 'treeHeight' && (
						// Ground to peak — covers both trunk and crown, since
						// treeHeight is the tree's total (ground-to-peak) extent.
						<VerticalMeasure
							x={cx + Math.max(trunkBottomHalfW, crownHalfW) + ARROW_GAP}
							yTop={crownTopY}
							yBottom={groundY}
							label={`${treeHeightFt} ft`}
						/>
					)}
					{activeField === 'crownHeight' && (
						<VerticalMeasure x={cx + crownHalfW + ARROW_GAP} yTop={crownTopY} yBottom={crownArrowBottomY} label={`${crownHeightFt} ft`} />
					)}
					{activeField === 'trunkDiameter' && (
						// A little below the ground line so the arrow has visible
						// separation from the trunk instead of sitting flush against it.
						<HorizontalMeasure y={groundY + 8} xLeft={cx - trunkBottomHalfW} xRight={cx + trunkBottomHalfW} label={`${trunkDiameterFt} ft`} labelAbove />
					)}
					{activeField === 'crownDiameter' && (
						<HorizontalMeasure y={crownTopY} xLeft={cx - crownHalfW} xRight={cx + crownHalfW} label={`${crownDiameterFt} ft`} labelAbove />
					)}
				</g>
			</TreePreviewSvg>
		</TreePreviewWrap>
	);
}

function labelBoxWidth(label: string) {
	return label.length * 5.4 + 8;
}

function VerticalMeasure({ x, yTop, yBottom, label }: { x: number; yTop: number; yBottom: number; label: string }) {
	const midY = (yTop + yBottom) / 2;
	const boxW = labelBoxWidth(label);
	// Keep the two arrowheads from overlapping/inverting when the measured
	// span renders very small (e.g. a thin trunk) — clamp to a minimum
	// visual length without changing the displayed value.
	const halfSpan = Math.max((yBottom - yTop) / 2 - 3, MIN_ARROW_HALF_SPAN);
	// The label normally floats to the right of the line, but a wide tree
	// pushes the line itself close to the right edge — once the label would
	// run into the padding (and get clipped), flip it to the line's left
	// instead so it stays fully visible.
	const flip = x + 4 + boxW > VIEW_SIZE - PADDING / 2;
	const boxX = flip ? x - 4 - boxW : x + 4;
	return (
		<g>
			<line
				x1={x} y1={midY - halfSpan} x2={x} y2={midY + halfSpan}
				stroke={ARROW_COLOR} strokeWidth={1.5}
				markerStart="url(#tree-preview-arrowhead)"
				markerEnd="url(#tree-preview-arrowhead)"
			/>
			<rect x={boxX} y={midY - 8} width={boxW} height={16} rx={3} fill="var(--defaultBgColor)" stroke="var(--cardBorderColor)" />
			<text x={boxX + boxW / 2} y={midY + 4} fontSize={11} fontFamily="Inter" fill="var(--textColor)" textAnchor="middle">{label}</text>
		</g>
	);
}

function HorizontalMeasure({ y, xLeft, xRight, label, labelAbove = false }: { y: number; xLeft: number; xRight: number; label: string; labelAbove?: boolean }) {
	const midX = (xLeft + xRight) / 2;
	const boxW = labelBoxWidth(label);
	const labelY = labelAbove ? y - 14 : y + 14;
	// Same overlap/inversion guard as VerticalMeasure, for thin/short spans.
	const halfSpan = Math.max((xRight - xLeft) / 2 - 3, MIN_ARROW_HALF_SPAN);
	return (
		<g>
			<line
				x1={midX - halfSpan} y1={y} x2={midX + halfSpan} y2={y}
				stroke={ARROW_COLOR} strokeWidth={1.5}
				markerStart="url(#tree-preview-arrowhead)"
				markerEnd="url(#tree-preview-arrowhead)"
			/>
			<rect x={midX - boxW / 2} y={labelY - 8} width={boxW} height={16} rx={3} fill="var(--defaultBgColor)" stroke="var(--cardBorderColor)" />
			<text x={midX} y={labelY + 4} fontSize={11} fontFamily="Inter" fill="var(--textColor)" textAnchor="middle">{label}</text>
		</g>
	);
}
